import { z } from 'zod';

export const PromoScopeSchema = z.enum(['PLATFORM', 'TENANT']);
export const PromoUsageSchema = z.enum(['GLOBAL', 'SINGLE_USE']);
export const PromoBenefitSchema = z.enum(['FIRST_BOOKING_FREE', 'PERCENTAGE']);
export const PromoEligibilitySchema = z.enum(['ANY_BOOKING', 'FIRST_BOOKING']);
export const PromoCoverageSchema = z.enum(['WHOLE_RESERVATION', 'FIRST_SLOT', 'FIRST_N_SLOTS']);

const optionalDate = z.string().datetime().nullable().optional();

export const CreatePromoCodeInputSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(3)
      .max(32)
      .regex(/^[A-Za-z0-9-]+$/),
    scope: PromoScopeSchema,
    tenantId: z.string().uuid().nullable().optional(),
    usage: PromoUsageSchema,
    maxUsesPerCustomer: z.number().int().min(1).max(2_147_483_647).default(1),
    benefit: PromoBenefitSchema,
    discountPercent: z.number().int().min(1).max(99).nullable().optional(),
    eligibility: PromoEligibilitySchema,
    coverage: PromoCoverageSchema,
    coveredSlotCount: z.number().int().min(2).max(24).nullable().optional(),
    validFrom: optionalDate,
    expiresAt: optionalDate,
  })
  .superRefine((value, ctx) => {
    if (
      value.maxUsesPerCustomer > 1 &&
      (value.usage === 'SINGLE_USE' || value.eligibility === 'FIRST_BOOKING')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maxUsesPerCustomer'],
        message: 'Repeated use requires a per-customer promo valid for any booking',
      });
    }
    if (value.scope === 'TENANT' && !value.tenantId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tenantId'],
        message: 'Tenant is required',
      });
    }
    if (value.scope === 'PLATFORM' && value.tenantId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tenantId'],
        message: 'Platform promos cannot select a tenant',
      });
    }
    if (value.benefit === 'PERCENTAGE' && value.discountPercent == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discountPercent'],
        message: 'Discount percent is required',
      });
    }
    if (value.benefit === 'FIRST_BOOKING_FREE' && value.discountPercent != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discountPercent'],
        message: 'Free booking promos cannot set a percentage',
      });
    }
    if (value.benefit === 'FIRST_BOOKING_FREE' && value.eligibility !== 'FIRST_BOOKING') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eligibility'],
        message: 'Free booking promos must target the first booking',
      });
    }
    if (value.coverage === 'FIRST_N_SLOTS' && value.coveredSlotCount == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['coveredSlotCount'],
        message: 'Covered slot count is required',
      });
    }
    if (value.coverage !== 'FIRST_N_SLOTS' && value.coveredSlotCount != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['coveredSlotCount'],
        message: 'Covered slot count is only valid for first-N-slots coverage',
      });
    }
    if (
      value.validFrom &&
      value.expiresAt &&
      new Date(value.validFrom) >= new Date(value.expiresAt)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expiresAt'],
        message: 'Expiry must be after valid-from',
      });
    }
  });

export const UpdatePromoCodeStatusInputSchema = z.object({ isActive: z.boolean() });

export const ListPromoCodesQuerySchema = z.object({
  status: z.enum(['SCHEDULED', 'ACTIVE', 'DEACTIVATED', 'EXPIRED', 'EXHAUSTED']).optional(),
  scope: PromoScopeSchema.optional(),
  tenantId: z.string().uuid().optional(),
  q: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export type CreatePromoCodeInput = z.infer<typeof CreatePromoCodeInputSchema>;
export type UpdatePromoCodeStatusInput = z.infer<typeof UpdatePromoCodeStatusInputSchema>;
export type ListPromoCodesQuery = z.infer<typeof ListPromoCodesQuerySchema>;
